import { useEffect, useId, useRef, type ReactNode } from "react";
import { cn } from "../cn";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * Whether a backdrop click or Escape closes the dialog. Defaults to true.
   *
   * Set false only when dismissing costs the user something they cannot get
   * back — a credential secret shown once, an unsaved draft. It is deliberately
   * awkward to reach for: a modal that ignores Escape is hostile everywhere
   * except the handful of places where losing the content is worse.
   */
  dismissible?: boolean;
  /**
   * `card` is one 480px card with a 16px radius and 24px padding: no rules, a
   * `text-title` title over a `text-meta` line, a borderless 30px close mark,
   * the content and the actions 16px apart and the actions right-aligned,
   * over a lighter scrim. Opt-in; `panel` is the default.
   */
  look?: "panel" | "card";
  /** `card` only: draw the close mark. Off for a dialog that has its own Cancel. */
  closeButton?: boolean;
  /** `card` only: classes for the block holding the title and the line under it. */
  headingClassName?: string;
  /** `card` only: classes for the title, which win over its defaults. */
  titleClassName?: string;
  /** `card` only: classes for the line under the title, which win over its defaults. */
  descriptionClassName?: string;
  /** `card` only: classes for the close mark, which win over its defaults. */
  closeClassName?: string;
}

/**
 * A modal dialog built on the native <dialog> element.
 *
 * Native gives focus trapping, Escape to close, inert background and the
 * top-layer stacking that a div-based modal has to reimplement — and usually
 * reimplements incompletely, which is how a keyboard user ends up tabbing
 * behind an open modal.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  dismissible = true,
  look = "panel",
  closeButton = true,
  headingClassName,
  titleClassName,
  descriptionClassName,
  closeClassName,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const cardTitleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handle = () => {
      onClose();
    };
    // Escape fires "cancel" before "close" on a native <dialog>, so preventing
    // it there is what actually stops the dismissal — intercepting "close"
    // would fire after the browser has already closed the element.
    const cancel = (e: Event) => {
      if (!dismissible) e.preventDefault();
    };
    el.addEventListener("close", handle);
    el.addEventListener("cancel", cancel);
    return () => {
      el.removeEventListener("close", handle);
      el.removeEventListener("cancel", cancel);
    };
  }, [onClose, dismissible]);

  if (look === "card") {
    return (
      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
      <dialog
        ref={ref}
        aria-labelledby={cardTitleId}
        className={cn(
          "m-auto max-h-[min(90dvh,52rem)] w-[min(480px,calc(100%-2rem))] max-w-none overflow-y-auto rounded-2xl border border-rule bg-surface p-0 text-ink shadow-float",
          "backdrop:bg-scrim-sheet",
          className,
        )}
        onClick={(e) => {
          if (dismissible && e.target === ref.current) onClose();
        }}
      >
        {/* The padding is inside, so a click on it is a click on the card, not on the scrim. */}
        <div className="relative flex flex-col gap-4 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className={cn("flex min-w-0 flex-col gap-1.5", headingClassName)}>
              <h2
                id={cardTitleId}
                className={cn(
                  "text-title leading-[1.35] font-semibold tracking-normal text-ink",
                  titleClassName,
                )}
              >
                {title}
              </h2>
              {description ? (
                <p className={cn("text-meta text-ink-3", descriptionClassName)}>
                  {description}
                </p>
              ) : null}
            </div>
            {dismissible && closeButton ? (
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className={cn(
                  "grid size-7.5 shrink-0 place-items-center rounded-[var(--radius-control)] text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                  closeClassName,
                )}
              >
                <CloseMark />
              </button>
            ) : null}
          </div>
          {children}
          {footer ? <div className="flex items-center justify-end gap-2">{footer}</div> : null}
        </div>
      </dialog>
    );
  }

  return (
    // These two rules assume a click handler with no keyboard route out. That
    // premise is false for a native <dialog>: Escape closes it, handled by the
    // browser and wired to onClose through the "close" listener above.
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
    <dialog
      ref={ref}
      aria-labelledby="dialog-title"
      className={cn(
        "m-auto w-full max-w-md rounded-[var(--radius-card)] border border-rule bg-surface p-0 text-ink",
        // Three rows: a pinned heading, a body that scrolls, a pinned footer.
        //
        // The whole element used to scroll, which is fine for a dialog that
        // asks one question and wrong for one that carries a form. Live
        // events' "Describe something new" is nine hundred pixels of editor,
        // and its Cancel and Add sat at the very top — so the control that
        // submits the form was off screen for the whole of filling it in.
        // `minmax(0,1fr)` on the middle row is what lets it shrink; without it
        // a grid row refuses to go below its content and nothing scrolls.
        //
        // **`open:grid`, never a bare `grid`.** A closed `<dialog>` is hidden
        // by `display: none` in the UA stylesheet and by nothing else, so a
        // display utility on this element overrides it and every dialog in the
        // console renders inline, closed, in the middle of its page. The AI
        // Studio's frame caught it — it asserts the document never scrolls, and
        // a closed dialog had just made it 184px taller than the viewport.
        "max-h-[min(85dvh,52rem)] grid-rows-[auto_minmax(0,1fr)_auto] open:grid",
        // A scrim darkens whatever is behind it rather than being a themed
        // surface, which is the one place an absolute is the right answer.
        "backdrop:bg-black/40", // token-exempt: see above
        className,
      )}
      // Clicking the backdrop closes: the dialog fills its own box, so a click
      // landing on the element itself came from outside the content.
      onClick={(e) => {
        if (dismissible && e.target === ref.current) onClose();
      }}
    >
      <div className="flex items-start justify-between gap-4 border-b border-rule px-5 py-4">
        <div className="flex flex-col gap-1">
          <h2 id="dialog-title" className="font-sans text-prose font-semibold">
            {title}
          </h2>
          {description ? <p className="text-prose text-ink-2">{description}</p> : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid size-8 shrink-0 place-items-center rounded-[var(--radius-control)] border border-rule text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              className="size-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            >
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        ) : null}
      </div>
      {children ? <div className="overflow-y-auto px-5 py-4">{children}</div> : null}
      {footer ? (
        <div className="flex items-center justify-end gap-2 border-t border-rule px-5 py-3">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}

function CloseMark() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
